import rtk, { _ } from 'rtk';
import { createActionsHook } from 'rtk/react';
import { name } from './constants';
import { getResourceMap } from './utils/resourceMap';

// Initialize RTK action + thunk creators
const { createActions, createThunks } = rtk(name);

/**
 * -----------------------
 * Document Actions
 * -----------------------
 */
const documentActions = createActions({
  addEventListener: (uuid, { event, type, id }, action) =>
    _({ uuid, subscription: { event, type, id }, action }),

  removeEventListeners: (uuid) => _({ uuid }),

  documentOpened: (uuid) => _({ uuid }),
  documentClosed: (uuid) => _({ uuid }),

  documentCanceled: (uuid) => _({ uuid }),

  documentDirty: (uuid) => _({ uuid }),

  resourceDirty: ({ type, id }) => _({ type, id }),

  resourceUpdated: (uuid, { type, id }) => _({ uuid, type, id }),
});

/**
 * -----------------------
 * Model Action Helpers
 * -----------------------
 */
const createModelActions = (model) => ({
  /**
   * Fetch all items, optionally limited.
   * If no limit, determine total count first to fetch all.
   */
  all: async (limit) => {
    if (limit) return model.per(limit).all();

    const { meta } = await model.per(0).stats({ total: 'count' }).all();
    return model.per(meta.stats.total.count).all();
  },

  first: () => model.first(),

  find: (id) => model.find(id),

  paginate: ({ page, per }) =>
    model.page(page).per(per).stats({ total: 'count' }).all(),
});

/**
 * Utility to run a specific action on a model
 */
const runModelAction =
  (model) =>
  ({ verb, param }) =>
    createModelActions(model)[verb](param);

/**
 * -----------------------
 * Document Thunks
 * -----------------------
 */
const documentApi = createThunks({
  /**
   * Fetch a document with given filters and actions
   */
  fetchDocument: async ({ uuid, request, options }, { getState, dispatch }) => {
    const { action, type, filter } = request;

    let model = getResourceMap(type);
    for (const [key, value] of Object.entries(filter || {})) {
      model = model[key](value);
    }

    const { raw } = await runModelAction(model)(action);
    return raw;
  },

  /**
   * Fetch a specific resource by type and ID
   */
  fetchResource: async ({ type, id }) => {
    const Model = getResourceMap(type);
    const { raw } = await Model.find(id);
    return raw.data;
  },
});

/**
 * -----------------------
 * Exports
 * -----------------------
 */

// RTK-AUTOEXPORT
// ---- actions ---------
export const {
  addEventListener,
  removeEventListeners,
  documentOpened,
  documentClosed,
  documentCanceled,
  documentDirty,
  resourceDirty,
  resourceUpdated,
} = documentActions;

export const useDocumentActions = createActionsHook(documentActions);
// ---- thunks ---------
export const { fetchDocument, fetchResource } = documentApi;

export const useDocumentApi = createActionsHook(documentApi);
